/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */
import {
  Amounts,
  HttpStatusCode,
  TalerError,
  TalerErrorCode,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  AsyncButton,
  Loading,
  RouteDefinition,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";

import { UserAndToken } from "@gnu-taler/taler-util";
import { useBankChallengeHandlerContext } from "../../context/challenge.js";
import {
  revalidateAccountDetails,
  useAccountDetails,
} from "../../hooks/account.js";
import { useSessionState } from "../../hooks/session.js";
import { OperationError } from "../../components/OperationError.js";
import { undefinedIfEmpty } from "../../utils.js";
import { LoginForm } from "../LoginForm.js";
import { doAutoFocus } from "../PaytoWireTransferForm.js";
import { RetryableError } from "../../components/RetryableError.js";

export function RemoveAccount({
  account,
  routeCancel,
  onUpdateSuccess,

  focus,
}: {
  focus?: boolean;

  routeCancel: RouteDefinition;
  onUpdateSuccess: () => void;
  account: string;
}): VNode {
  const { i18n } = useTranslationContext();
  const result = useAccountDetails(account);
  const [accountName, setAccountName] = useState<string | undefined>();

  const { state } = useSessionState();
  const token = state.status !== "loggedIn" ? undefined : state.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();
  const { showError } = useNotificationContext();
  const mfa = useBankChallengeHandlerContext();

  const errors = undefinedIfEmpty({
    accountName: !accountName
      ? i18n.str`Required`
      : account !== accountName
        ? i18n.str`Name doesn't match`
        : undefined,
  });

  // i18n.str`delete account`,
  const deleteAccount = useNotifiedOperation<
    Awaited<ReturnType<typeof api.deleteAccount>>,
    [UserAndToken, challengeIds?: string[]]
  >(
    (ct, auth: UserAndToken, challengeIds?: string[]) =>
      api.deleteAccount(auth, { challengeIds }),
    {
      onSuccess: () => {
        mfa.cancel();
        // notifyInfo(i18n.str`Account removed`);
        onUpdateSuccess();
      },
      onFail: showError(
        i18n.str`Failed to delete the account.`,
        (fail, creds) => {
          switch (fail.case) {
            case HttpStatusCode.Unauthorized:
              return i18n.str`Not enough permission to delete the account.`;
            case HttpStatusCode.NotFound:
              return i18n.str`The username was not found.`;
            case TalerErrorCode.BANK_RESERVED_USERNAME_CONFLICT:
              return i18n.str`Can't delete a reserved username.`;
            case TalerErrorCode.BANK_ACCOUNT_BALANCE_NOT_ZERO:
              return i18n.str`Can't delete an account with balance different than zero.`;
            case HttpStatusCode.Accepted:
              mfa.onNewChallenge(
                i18n.str`Account deletion`,
                creds.username,
                fail.body,
                {
                  running: deleteAccount.running,
                  cancel: deleteAccount.cancel,
                  run: (challengeIds) => deleteAccount.run(creds, challengeIds),
                },
              );
              return undefined;
            default:
              assertUnreachable(fail);
          }
        },
      ),
    },
  );

  if (!result) {
    return <Loading />;
  }
  if (result instanceof TalerError) {
    return (
      <Fragment>
        <RetryableError
          error={result}
          title={i18n.str`Failed to load account details.`}
          onRetry={() => void revalidateAccountDetails()}
        />
        <LoginForm currentUser={account} />
      </Fragment>
    );
  }
  if (result.type === "fail") {
    switch (result.case) {
      case HttpStatusCode.Unauthorized:
        return <LoginForm currentUser={account} />;
      case HttpStatusCode.NotFound:
        return <LoginForm currentUser={account} />;
      default:
        assertUnreachable(result);
    }
  }

  const balance = Amounts.parse(result.body.balance.amount);
  if (!balance) {
    return (
      <i18n.Translate>there was an error reading the balance</i18n.Translate>
    );
  }
  const isBalanceEmpty = Amounts.isZero(balance);
  if (!isBalanceEmpty) {
    return (
      <Fragment>
        <Attention type="warning" title={i18n.str`Can't delete the account`}>
          <i18n.Translate>
            The account can't be deleted while still holding some balance. First
            make sure that the owner makes a complete cashout.
          </i18n.Translate>
        </Attention>
        <div class="mt-5 sm:mt-6">
          <a
            href={routeCancel.url({})}
            data-name="close"
            class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <i18n.Translate>Close</i18n.Translate>
          </a>
        </div>
      </Fragment>
    );
  }

  return (
    <section class="mt-2">
      <a
        href={routeCancel.url({})}
        class="inline-flex items-center text-sm font-semibold text-brand hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Go back</i18n.Translate>
      </a>
      <div class="mx-auto mt-6 max-w-2xl">
        <h1 class="text-2xl font-semibold text-brand">
          <i18n.Translate>Delete account "{account}"</i18n.Translate>
        </h1>
        <div class="mt-6">
          <Attention
            type="warning"
            title={i18n.str`This action cannot be undone`}
          >
            <i18n.Translate>
              All account data will be permanently removed.
            </i18n.Translate>
          </Attention>
        </div>

        <form
          class="mt-6 overflow-hidden rounded-xl border border-onBackground/10 bg-white shadow-sm"
          autoCapitalize="none"
          autoCorrect="off"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div class="px-4 py-6 sm:p-8">
            <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div class="sm:col-span-5">
                <label
                  class="block text-sm font-medium leading-6 text-onBackground"
                  for="password"
                >
                  {i18n.str`Verification`}
                </label>
                <div class="mt-2">
                  <input
                    ref={focus ? doAutoFocus : undefined}
                    type="text"
                    class="block w-full rounded-md border-0 py-1.5 text-onBackground shadow-sm ring-1 ring-inset ring-gray-300 data-[error=true]:ring-red-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    name="password"
                    id="password"
                    data-error={
                      !!errors?.accountName && accountName !== undefined
                    }
                    value={accountName ?? ""}
                    onChange={(e) => {
                      setAccountName(e.currentTarget.value);
                    }}
                    placeholder={account}
                    autocomplete="off"
                  />
                  <ShowInputErrorLabel
                    message={errors?.accountName}
                    isDirty={accountName !== undefined}
                  />
                </div>
                <p class="mt-2 text-sm text-gray-500">
                  <i18n.Translate>
                    Enter the account name that is going to be deleted
                  </i18n.Translate>
                </p>
              </div>
            </div>
          </div>
          <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
            <OperationError class="mb-4" />
            <div class="flex items-center justify-between gap-x-6">
              <a
                href={routeCancel.url({})}
                data-name="cancel"
                class="text-sm font-semibold leading-6 text-onBackground"
              >
                <i18n.Translate>Cancel</i18n.Translate>
              </a>
              <AsyncButton
                submit
                name="delete"
                class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                disabled={!!errors || !token}
                onClick={() =>
                  deleteAccount.run({ username: account, token: token! })
                }
              >
                <i18n.Translate>Delete account</i18n.Translate>
              </AsyncButton>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
